import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { PageBody } from '../../components/Page';
import { Badge } from '../../components/Pill';
import { useShopFile } from '../../components/ShopFileLayout';
import { StatusBanner } from '../../components/StatusBanner';
import { intlLocale } from '../../i18n/formats';
import { useLocale } from '../../i18n/LocaleProvider';
import { isShopAccessRequired, opsErrorMessage } from '../../lib/ops-api';
import {
  fetchSmtpTestStatus,
  listEmailLog,
  OpsEmailFilterStatus,
  opsErrorCode,
  sendSmtpTest,
  type OpsSmtpTestResult,
} from '../../lib/ops-documents-api';
import {
  SMTP_TEST_POLL_MAX,
  SMTP_TEST_POLL_MS,
  emailKindKey,
  emailStatusKey,
  emailStatusTone,
  errorKeyForCode,
  isSmtpTestSettled,
  notLoggedKey,
  smtpTestOutcomeKey,
  smtpTestStatusKey,
} from '../../lib/ops-documents';
import { REASON_MIN_LENGTH, isReasonAcceptable } from '../../lib/shop-file';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

/**
 * The status filter's options. `BOUNCED` is not among them: nothing records a bounce (D5), so the
 * filter would only ever answer "none" and read as a finding.
 */
const STATUS_FILTERS: ReadonlyArray<OpsEmailFilterStatus | ''> = [
  '',
  OpsEmailFilterStatus.Sent,
  OpsEmailFilterStatus.Failed,
  OpsEmailFilterStatus.Retrying,
  OpsEmailFilterStatus.Processing,
  OpsEmailFilterStatus.Pending,
];

/** A date-time in the reader's locale. */
function when(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/**
 * The shop's email (work order item 4): the delivery log, and a test mail through the merchant's
 * SMTP to the operator's own address.
 *
 * **What the log can say, and what it cannot** (AC-4.3, AC-4.4). `SENT` means the mail server
 * accepted the message — never shown as "delivered" — and the log only holds document and export
 * mail, so an empty result is explained rather than presented as "never sent".
 */
export function EmailTab() {
  const { t, tCount, locale } = useLocale();
  const { shopId, file, requireAccess } = useShopFile();
  const [recipient, setRecipient] = useState('');
  const [debouncedRecipient] = useDebouncedValue(recipient);
  const [status, setStatus] = useState<OpsEmailFilterStatus | ''>('');
  const [page, setPage] = useState(1);

  useEffect(() => setPage(1), [debouncedRecipient, status]);

  const log = useQuery({
    queryKey: [
      'ops-email-log',
      shopId,
      debouncedRecipient.trim(),
      status,
      page,
    ],
    queryFn: () =>
      listEmailLog(shopId, {
        recipient: debouncedRecipient,
        ...(status ? { status } : {}),
        page,
      }),
    retry: false,
  });

  useEffect(() => {
    if (log.isError && isShopAccessRequired(log.error, shopId)) {
      requireAccess();
    }
  }, [log.isError, log.error, shopId, requireAccess]);

  const totalPages = log.data
    ? Math.max(1, Math.ceil(log.data.total / log.data.pageSize))
    : 1;
  const notLogged = (log.data?.notLogged ?? [])
    .map((token) => notLoggedKey(token))
    .filter((key): key is string => key !== null)
    .map((key) => t(key));

  return (
    <PageBody>
      <SmtpTestCard
        shopId={shopId}
        usesFallbackEmail={file.setup.usesFallbackEmail}
        onAccessRequired={requireAccess}
      />

      <section className="card space-y-3">
        <h2 className="text-base font-semibold text-gray-900">
          {t('opsEmail.log.title')}
        </h2>
        <StatusBanner tone="info">
          <div className="space-y-1">
            <p>{t('opsEmail.log.acceptedMeaning')}</p>
            <p>
              {t('opsEmail.log.notLoggedLead', {
                list: notLogged.join(', ') || '—',
              })}
            </p>
            {log.data?.usesFallbackEmail && (
              <p>{t('opsEmail.log.fallbackRelay')}</p>
            )}
          </div>
        </StatusBanner>

        <div className="flex flex-wrap gap-3 items-end">
          <div className="grow min-w-[min(16rem,100%)]">
            <label htmlFor="opsEmailRecipient" className="field-label">
              {t('opsEmail.log.recipientLabel')}
            </label>
            <input
              id="opsEmailRecipient"
              type="search"
              value={recipient}
              onChange={(event) => setRecipient(event.target.value)}
              placeholder={t('opsEmail.log.recipientPlaceholder')}
              maxLength={254}
              className="field-input"
            />
          </div>
          <div>
            <label htmlFor="opsEmailStatus" className="field-label">
              {t('opsEmail.log.statusLabel')}
            </label>
            <select
              id="opsEmailStatus"
              value={status}
              onChange={(event) => {
                const next = STATUS_FILTERS.find(
                  (option) => option === event.target.value,
                );
                setStatus(next ?? '');
              }}
              className="field-input"
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option || 'all'} value={option}>
                  {option || t('opsEmail.log.statusAll')}
                </option>
              ))}
            </select>
          </div>
        </div>

        {log.isLoading ? (
          <StatusBanner tone="loading">{t('common.loading')}</StatusBanner>
        ) : log.isError ? (
          <StatusBanner tone="error">
            {opsErrorMessage(log.error, t('opsEmail.log.loadFailed'))}
          </StatusBanner>
        ) : log.data && log.data.data.length > 0 ? (
          <>
            <p className="text-sm text-gray-600">
              {tCount('opsEmail.log.count', log.data.total)}
            </p>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('opsEmail.log.columns.attempted')}</th>
                    <th>{t('opsEmail.log.columns.about')}</th>
                    <th>{t('opsEmail.log.columns.recipient')}</th>
                    <th>{t('opsEmail.log.columns.status')}</th>
                    <th>{t('opsEmail.log.columns.attempts')}</th>
                    <th>{t('opsEmail.log.columns.error')}</th>
                  </tr>
                </thead>
                <tbody>
                  {log.data.data.map((row) => {
                    const meaning = emailStatusKey(row.status);
                    return (
                      <tr key={row.id}>
                        <td className="whitespace-nowrap">
                          {when(row.lastAttemptAt, intlLocale(locale))}
                        </td>
                        <td>
                          {t(emailKindKey(row.kind))}
                          {row.documentNumber && (
                            <span className="font-mono text-xs">
                              {' '}
                              {row.documentNumber}
                            </span>
                          )}
                          <div
                            className="text-xs text-gray-500 max-w-xs truncate"
                            title={row.subject}
                          >
                            {row.subject}
                          </div>
                        </td>
                        <td className="text-xs break-all">
                          {row.recipientEmail}
                        </td>
                        <td>
                          <Badge tone={emailStatusTone(row.status)}>
                            {row.status}
                          </Badge>
                          {meaning && (
                            <div className="text-xs text-gray-600 mt-1">
                              {t(meaning)}
                            </div>
                          )}
                          {row.acceptedAt && (
                            <div className="text-xs text-gray-500">
                              {t('opsEmail.log.acceptedAt', {
                                at: when(row.acceptedAt, intlLocale(locale)),
                              })}
                            </div>
                          )}
                        </td>
                        <td>{row.attemptCount}</td>
                        <td className="text-xs text-red-700 max-w-md">
                          {row.errorMessage ?? ''}
                          {row.errorCode && (
                            <div className="font-mono text-gray-500">
                              {row.errorCode}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn-neutral btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => current - 1)}
                >
                  {t('opsEmail.log.previous')}
                </button>
                <span className="text-sm text-gray-600">
                  {t('opsEmail.log.page', { page, pages: totalPages })}
                </span>
                <button
                  type="button"
                  className="btn-neutral btn-sm"
                  disabled={page >= totalPages || page >= 200}
                  onClick={() => setPage((current) => current + 1)}
                >
                  {t('opsEmail.log.next')}
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-600">{t('opsEmail.log.empty')}</p>
        )}
      </section>
    </PageBody>
  );
}

/**
 * The SMTP test: through the merchant's server, to the signed-in operator only (A11).
 *
 * There is no recipient field on purpose — the API takes the address from the operator's token,
 * never from the request, and names it in its answer; the dashboard does not decode the token to
 * show it up front. On our fallback relay the test is not applicable (AC-4.5): it would
 * probe our infrastructure, not the merchant's, so the button explains that instead of sending.
 */
function SmtpTestCard({
  shopId,
  usesFallbackEmail,
  onAccessRequired,
}: {
  readonly shopId: string;
  readonly usesFallbackEmail: boolean;
  readonly onAccessRequired: () => void;
}) {
  const { t } = useLocale();
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [queued, setQueued] = useState<OpsSmtpTestResult | null>(null);

  const send = useMutation({
    mutationFn: () => sendSmtpTest(shopId, reason.trim()),
    onSuccess: (data) => {
      setConfirming(false);
      setQueued(data);
    },
    onError: (error) => {
      setConfirming(false);
      if (isShopAccessRequired(error, shopId)) onAccessRequired();
    },
  });

  const queryClient = useQueryClient();
  const jobId = queued?.jobId ?? null;
  const pollKey = ['ops-smtp-test', shopId, jobId];
  const poll = useQuery({
    queryKey: pollKey,
    queryFn: () => fetchSmtpTestStatus(shopId, jobId ?? ''),
    enabled: jobId !== null,
    retry: false,
    refetchInterval: (query) => {
      const current: string | undefined = query.state.data?.status;
      if (current && isSmtpTestSettled(current)) return false;
      if (query.state.dataUpdateCount >= SMTP_TEST_POLL_MAX) return false;
      return SMTP_TEST_POLL_MS;
    },
  });

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    if (!isReasonAcceptable(reason)) return;
    setQueued(null);
    setConfirming(true);
  }

  const sendErrorKey = send.isError
    ? errorKeyForCode(opsErrorCode(send.error))
    : null;
  // Widened to string: the generated enums are TS enums, which a literal cannot be compared with.
  const pollStatus: string | undefined = poll.data?.status;
  const outcome: string | undefined = queued?.outcome;
  // The polls made so far live on the query's state, not on the hook's result.
  const polls = queryClient.getQueryState(pollKey)?.dataUpdateCount ?? 0;
  const gaveUp = pollStatus === 'queued' && polls >= SMTP_TEST_POLL_MAX;

  return (
    <section className="card space-y-3">
      <h2 className="text-base font-semibold text-gray-900">
        {t('opsEmail.smtpTest.title')}
      </h2>
      {usesFallbackEmail ? (
        <StatusBanner tone="info">
          {t('opsEmail.smtpTest.notApplicableFallbackRelay')}
        </StatusBanner>
      ) : (
        <>
          <p className="text-sm text-gray-600">{t('opsEmail.smtpTest.lead')}</p>
          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <label htmlFor="opsSmtpTestReason" className="field-label">
                {t('opsEmail.smtpTest.reasonLabel')}
              </label>
              <textarea
                id="opsSmtpTestReason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={2}
                maxLength={500}
                className="field-input"
              />
              <p className="field-hint">
                {t('opsEmail.smtpTest.reasonHint', { min: REASON_MIN_LENGTH })}
              </p>
            </div>
            <button
              type="submit"
              className="btn-primary btn-sm"
              disabled={!isReasonAcceptable(reason) || send.isPending}
            >
              {t('opsEmail.smtpTest.send')}
            </button>
          </form>
        </>
      )}

      {send.isError && (
        <StatusBanner tone="error">
          {sendErrorKey
            ? t(sendErrorKey)
            : opsErrorMessage(send.error, t('opsEmail.smtpTest.sendFailed'))}
        </StatusBanner>
      )}

      {queued && outcome && outcome !== 'queued' && (
        <StatusBanner tone="info">
          {t(smtpTestOutcomeKey(outcome))}
        </StatusBanner>
      )}

      {queued && outcome === 'queued' && (
        <StatusBanner
          tone={
            pollStatus === 'completed'
              ? 'success'
              : pollStatus === 'failed' || pollStatus === 'not_found'
                ? 'error'
                : gaveUp
                  ? 'warning'
                  : 'loading'
          }
        >
          <div className="space-y-1">
            <p>
              {t('opsEmail.smtpTest.queued', {
                recipient: queued.recipient ?? '—',
                auditId: queued.auditLogId ?? '—',
              })}
            </p>
            <p>
              {gaveUp
                ? t('opsEmail.smtpTest.gaveUp')
                : t(smtpTestStatusKey(pollStatus ?? 'queued'))}
            </p>
            {pollStatus === 'failed' &&
              (poll.data?.errorKey || poll.data?.error) && (
                <p className="font-mono text-xs">
                  {poll.data?.errorKey ?? poll.data?.error}
                </p>
              )}
          </div>
        </StatusBanner>
      )}

      <ConfirmDialog
        open={confirming}
        title={t('opsEmail.smtpTest.confirmTitle')}
        confirmLabel={t('opsEmail.smtpTest.confirmLabel')}
        confirmDisabled={send.isPending}
        onConfirm={() => send.mutate()}
        onCancel={() => setConfirming(false)}
      >
        <p>{t('opsEmail.smtpTest.confirmBody')}</p>
      </ConfirmDialog>
    </section>
  );
}
